import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SiteActions, SiteState } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { selectNutrientBalanceReport } from 'src/app/advice/store/fertilization-result.selectors';
import { CropFieldAdvice, NutrientBalanceTable } from 'src/app/shared/models';
import { GetFertilizationTableReport } from 'src/app/store/actions';
import {
  getCropYear,
  getReportSort,
  selectCropFieldAdvices,
  selectLoggedInEmployeeAsArray,
} from 'src/app/store/selectors';

import { ReportSortService } from '../services/report-sort.service';

@Component({
  selector: 'app-nutrient-balance-report',
  templateUrl: './nutrient-balance-report.component.html',
  styleUrls: ['./../reports.scss'],
  standalone: false,
})
export class NutrientBalanceReportComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  protected readonly router = inject(Router);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  cropYear$ = this.store.pipe(select(getCropYear));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  pageTitle$ = combineLatest([this.cropYear$, this.customer$]).pipe(
    map(
      ([cropYear, customer]) =>
        `${customer?.name ? customer.name : ''} - ${this.locale.translate(
          'nutrientBalance',
        )} ${cropYear}`,
    ),
  );
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));
  cropFieldIds$ = this.store.pipe(select(RouterSelectors.getRouteParam('cropFieldIds')));
  tableData$: Observable<NutrientBalanceTable[]> = new Observable();
  sortedTableData$: Observable<NutrientBalanceTable[]> = new Observable();
  cropFieldAdvices$ = this.store.pipe(select(selectCropFieldAdvices));

  reportSort$ = this.store.pipe(select(getReportSort));

  buttonItems$ = this.sortService.getButtonItems();

  ngOnInit() {
    this.pageTitle$.pipe(takeUntil(this.destroyed$)).subscribe((pageTitle) => {
      this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
    });

    this.cropFieldAdvices$
      .pipe(withLatestFrom(this.cropFieldIds$), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(([cropFieldAdvices, fieldIdsString]) => {
        if (cropFieldAdvices.length && fieldIdsString.length) {
          const fieldIds = JSON.parse(fieldIdsString) as number[];
          const adviceIds: number[] = this.getAdviceIds(fieldIds, cropFieldAdvices);
          this.store.dispatch(new GetFertilizationTableReport(adviceIds, fieldIds));
          this.tableData$ = this.store.pipe(
            select(selectNutrientBalanceReport(adviceIds)),
            takeUntil(this.destroyed$),
          );
        }
      });

    this.reportSort$.subscribe(
      (reportSort) =>
        (this.sortedTableData$ = this.tableData$.pipe(
          map((tableData) => {
            const totals = tableData.filter((item) => item.metaData.isTotals);
            const sorted = this.sortService.sort({
              elements: tableData.filter((table) => !totals.includes(table)),
              option: reportSort.option,
              descending: reportSort.descending,
              cropFieldNameFunc: (item) => item.metaData.cropFieldName,
              cropNameFunc: (item) => item.metaData.cropName + ' ' + item.metaData.cropVarietyName,
            });
            return totals.concat(sorted);
          }),
        )),
    );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  print() {
    if (this.window) {
      this.window.print();
    }
  }

  back() {
    this.router.navigate(['/']);
  }

  getAdviceIds(fieldIds: number[], cropFieldAdvices: CropFieldAdvice[]) {
    const adviceIds: number[] = [];
    fieldIds.forEach((fieldId) => {
      const filteredCropFieldAdvices = cropFieldAdvices.filter((a) => a.cropFieldId === fieldId);
      const adviceId = filteredCropFieldAdvices.length
        ? filteredCropFieldAdvices[0].adviceId
        : null;
      if (adviceId) {
        adviceIds.push(adviceId);
      }
    });
    return adviceIds;
  }

  buttonAction(data: string) {
    this.sortService.handleButtonAction(data);
  }
}
